import { useEffect } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useRouteContext } from "@tanstack/react-router";
import { FiEdit, FiEye, FiPlus, FiSave } from "react-icons/fi";

import { PRGMAddEditForm } from "@/components/records/child/PRGMAddEditForm";
import Popup from "@/components/shared/Popup";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import { PRGM_TEMPLATE, type PRGM } from "@/models";
import { prgmMutationFunctions } from "@/models/chld";
import { useLeaData } from "@/queries.ts";

export type PRGMEditDialogProps = {
  selectedPrgm: PRGM | undefined;
  prgmDefaults?: Partial<PRGM>;
  onClose: () => void;
};

export default function PRGMEditDialog({
  selectedPrgm,
  prgmDefaults,
  onClose,
}: PRGMEditDialogProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const leaData = useLeaData(leaContext.currentUserOrg.esiIdPath).data;

  const isAdd = selectedPrgm == null;
  const canEdit = leaContext.hasRole("PRGMEdit");
  const initialValues: PRGM =
    selectedPrgm ?? { ...PRGM_TEMPLATE, ...prgmDefaults };

  const form = useForm<PRGM>({ defaultValues: initialValues });

  useEffect(() => {
    form.reset(selectedPrgm ?? { ...PRGM_TEMPLATE, ...prgmDefaults });
    form.clearErrors();
  }, [selectedPrgm, prgmDefaults, form]);

  const formId = "frmPRGM";

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form,
      recordType: "PRGM",
      toastMessage: "Education Program record submitted.\nWaiting for validation...",
      onSuccess: onClose,
    });

  const mutateFns = prgmMutationFunctions(leaData, setSubmitResult);

  const onSubmit: SubmitHandler<PRGM> = (x) => {
    handleSubmitStart();
    mutateFns.handleSave(x, false, "A");
  };

  const formMode = isAdd ? "add" : canEdit ? "edit" : "view";
  const headerIcon = isAdd ? (
    <FiPlus size={20} />
  ) : canEdit ? (
    <FiEdit size={20} />
  ) : (
    <FiEye size={20} />
  );
  const headerText = isAdd
    ? "Add Education Program Record"
    : canEdit
      ? "Edit Education Program Record"
      : "View Education Program Record";
  const submitButtonText = isAdd ? "Save" : "Update";

  return (
    <Popup
      show={true}
      header={
        <div
          className="d-flex align-items-center gap-2"
          style={{ fontSize: "1.25rem" }}
        >
          {headerIcon}
          <span>{headerText}</span>
        </div>
      }
      hasButton={false}
      submitFormId={canEdit ? formId : undefined}
      submitButtonText={
        <span className="d-flex align-items-center">
          <FiSave className="me-1" />
          {submitButtonText}
        </span>
      }
      submitButtonLoading={isSubmitting}
      submitButtonLoadingText="Submitting..."
      secondButtonText="Cancel"
      size="lg"
      onClose={onClose}
      onSecondButtonClick={onClose}
      canEdit={canEdit}
    >
      <Form id={formId} onSubmit={form.handleSubmit(onSubmit)}>
        <PRGMAddEditForm form={form} mode={formMode} />
      </Form>
    </Popup>
  );
}
